import { ReactElement } from 'react';
import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { FANCY_CONTAINER_CLASS_NAME } from '@components/icon-fancy-container/icon-fancy-container';
import { IconProps } from '@components/icons';
import { IconDocument } from '@components/icons/document';
import { IconDrag } from '@components/icons/drag';
import { IconForm } from '@components/icons/form';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconStarSolid } from '@components/icons/star-solid';
import { Menu } from '@components/menu';
import { Nav } from '@components/nav';
import { NavGroupCollapse } from '@components/nav-group-collapse/nav-group-collapse';
import { useNavContext } from '@components/nav/nav';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';
import { pxToRem } from '@utils/converters';

import { useUserOption } from 'api/authentication/hooks';
import { useCachedFavorites } from 'api/favorites/hooks';
import { CompanyUserFavorite, FavoriteContentType } from 'api/favorites/requests';
import { routePaths } from 'ui/authenticated/navigation';

import { RemoveFavoriteMenuItem } from '../favorites/favorites';

const contentTypeMap: Record<
  FavoriteContentType,
  {
    getLink: (props: { workspaceId: string; shortId: string; journeyId?: string }) => string;
    icon: ReactElement<IconProps>;
  }
> = {
  journey: {
    getLink: ({ shortId = '', workspaceId = '' }) => {
      return generatePath(routePaths.journeyDetails, {
        workspaceId,
        journeyId: shortId,
        '*': null,
      });
    },
    icon: <IconJourneyItem size={16} />,
  },
  documenttemplate: {
    getLink: ({ shortId, workspaceId }) => {
      return generatePath(routePaths.worksspaceDocumentDetail, {
        workspaceId,
        documentId: shortId,
        '*': null,
      });
    },
    icon: <IconDocument size={16} />,
  },
  journeyform: {
    getLink: ({ shortId, workspaceId, journeyId }) => {
      if (!journeyId) {
        return '';
      }
      return (
        generatePath(routePaths.editJourney, {
          workspaceId,
          journeyId,
          '*': 'journey',
        }) + `?openJourneyForm=${shortId}`
      );
    },
    icon: <IconForm size={16} />,
  },
};

const generateLinkItem = ({
  name,
  contentType,
  workspaceId,
  objectShortId,
  shortId,
  journeyShortId,
}: CompanyUserFavorite) => {
  const { getLink, icon } = contentTypeMap[contentType];

  return {
    href: getLink({ workspaceId, shortId: objectShortId, journeyId: journeyShortId }),
    icon: (
      <IconFancyContainer
        size="sm"
        icon={icon}
        hoverIcon={<IconDrag size={16} color="white100" />}
        variant="gray"
      />
    ),
    label: name,
    id: shortId,
    iconRight: (
      <Menu
        trigger={IconButton}
        triggerProps={{
          size: 'sm',
          variant: 'ghost-purple',
          icon: <IconMoreVertical size={16} />,
        }}
      >
        <RemoveFavoriteMenuItem shortId={shortId} />
      </Menu>
    ),
  };
};

const expandedStore = createSelectionStore({ persistKey: 'favorites-nav-state' });

export const Favorites = () => {
  const { t } = useScopedTranslate(locale);
  const favorites = useCachedFavorites();
  const { update } = useUserOption('navbarFavoritesOrder', []);
  const { isOpen } = useNavContext();

  if (!favorites?.length) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Stack justify="center" align="center" style={{ padding: `${pxToRem(8)} 0` }}>
          <IconStarSolid size={16} color="elementOrange100" />
        </Stack>
      )}
      <Container>
        <NavGroupCollapse
          id="main-navbar-favorites"
          label={t('favorites')}
          links={favorites.map(generateLinkItem)}
          toggleGroup={expandedStore.toggle}
          expandedSelectionStore={expandedStore}
          onDragEnd={update.mutate}
        />
        <Nav.Separator />
      </Container>
    </>
  );
};

const Container = styled.div({
  [`.${FANCY_CONTAINER_CLASS_NAME}`]: {
    height: pxToRem(16),
    width: pxToRem(16),
  },
});

const locale = {
  en: {
    favorites: 'Favourites',
  },
};
